import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { StateService } from '@adgate/common/services/state-service';
import { AuthenticationService } from '@adgate/common/services/authentication-service';
import { DialogService } from '@adgate/common/services/dialog-service';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ModuleActivationService } from '@adgate/common/services/module-activation-service';
import { AdGateConstants } from '@adgate/common/constants/adgate.constants';
import { environment } from '@env/environment';
import { defer, filter, from, map, switchMap } from 'rxjs';
import { ChangePasswordDialogComponent, ChangePasswordDialogResult } from '../../dialogs/change-password-dialog-component/change-password-dialog-component';

@Component({
  selector: 'app-login-component',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule
  ],
  templateUrl: './login-component.html',
  styleUrl: './login-component.scss',
})
export class LoginComponent implements OnInit
{
  private stateService: StateService = inject(StateService);
  private moduleActivationService: ModuleActivationService = inject(ModuleActivationService);
  private authService: AuthenticationService = inject(AuthenticationService);
  private dialogService: DialogService = inject(DialogService);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private formBuilder = inject(FormBuilder);

  public loginForm: FormGroup;

  constructor()
  {
    this.stateService.restore();

    if( this.authService.isUserAuthenticated() ) {
      this.dialogService.hideProgress();
      this.moduleActivationService.activateModule(AdGateConstants.MODULE_DASHBOARD);
    }

    this.loginForm = this.formBuilder.group({
      login: ['', Validators.required],
      password: ['', Validators.required]
    });
  }

  ngOnInit(): void 
  {
    this.route.queryParamMap.subscribe( params => {
      if ( params.has( "code" ) ) {
        this.onAuthenticateMyId(params.get("code")!);
      }

      if ( params.has( "prrc" ) ) {
        this.onChangePassword(params.get("prrc")!);
      }
    });
  }

  onLogin(): void
  {
    if( this.loginForm.valid ) {
      this.dialogService.showProgress();
      this.authService.authenticate(this.loginForm.get('login')?.value,this.loginForm.get('password')?.value).subscribe({
        next: (r:RESTApplicationResponse) => {
          this.dialogService.hideProgress();
          this.moduleActivationService.activateModule(AdGateConstants.MODULE_DASHBOARD);
        },
        error: (err:Error) => {
          this.dialogService.openErrorDialog(err.message);
        }
      });
    }
  }

  onLoginMyid(): void 
  {
    const now: Date = new Date();
    const challengeString: string = this.generateChallengeString();

    defer(() => {
      const data = new TextEncoder().encode(challengeString);
      return from(window.crypto.subtle.digest('SHA-256',data));
    }).pipe(
      map((digest:ArrayBuffer) => {
        const bytes = new Uint8Array(digest);
        const binString = String.fromCharCode(...bytes);
        return btoa(binString)
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=+$/, '');
      })
    ).subscribe((codeChallenge:string) => {
      const url: string = environment.myidAuthCodeUrl.replace("[STATE]", now.getTime().toString()).replace("[CODE_CHALLENGE]", codeChallenge);
      this.stateService.data.codeVerifier = challengeString;
      this.stateService.persist();
      this.dialogService.showProgress();
      document.location.href = url;
    });
  }

  onAuthenticateMyId(myIdAuthenticationCode: string)
  {
    this.dialogService.showProgress();

    this.authService.authenticateMyId(myIdAuthenticationCode, this.stateService.data.codeVerifier).subscribe({
      next: (res:RESTApplicationResponse) => {
        this.dialogService.hideProgress();
        this.moduleActivationService.activateModule(AdGateConstants.MODULE_DASHBOARD);
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  generateChallengeString()
  {
    const characters: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result: string = '';
    const charactersLength: number = characters.length;

    for (let i = 0; i < 128; i++) {
        const randomIndex: number = Math.floor(Math.random() * charactersLength);
        result += characters.charAt(randomIndex);
    }

    return result;
  }

  onRecoverPassword()
  {
    if( this.loginForm.get('login')?.valid ) {
      this.dialogService.showProgress();
      this.authService.startPasswordRecovery(this.loginForm.get('login')?.value).subscribe({
        next: (r:RESTApplicationResponse) => {
          this.dialogService.openInfoDialog(r.message);
        },
        error: (err:Error) => {
          this.dialogService.openErrorDialog(err.message);
        }
      })
    } else {
      this.loginForm.get('login')?.markAsTouched();
    }
  }

  private onChangePassword(passwordRecoveryCode: string)
  {
    let dialogData = {
      email: passwordRecoveryCode,
      disableCurrentPassword: true
    };

    this.dialogService.openAnyDialog(ChangePasswordDialogComponent,{data: dialogData, width: '600px' }).afterClosed().pipe(
      filter((result: ChangePasswordDialogResult | null) => result !== null),
      switchMap((result:ChangePasswordDialogResult) => {
        this.dialogService.showProgress();
        return this.authService.recoverPassword(passwordRecoveryCode, result.newPassword);
      })
    ).subscribe({
      next: (r:RESTApplicationResponse) => {
        this.dialogService.openInfoDialog(r.message);
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }
}
